// RIFF WAVE reader: PCM 8/16/24/32 bit and 32 bit float, mono or stereo,
// to float samples for Web Audio. ET's sounds are 16 bit PCM at 11/22/44 kHz.

export interface PcmSound {
  sampleRate: number;
  /** One array of samples (-1..1) per channel. */
  channels: Float32Array[];
}

const FORMAT_PCM = 1;
const FORMAT_FLOAT = 3;
const FORMAT_EXTENSIBLE = 0xfffe;

const chunkId = (d: Uint8Array, o: number) => String.fromCharCode(d[o], d[o + 1], d[o + 2], d[o + 3]);

export function parseWav(data: Uint8Array): PcmSound {
  if (data.length < 12 || chunkId(data, 0) !== 'RIFF' || chunkId(data, 8) !== 'WAVE') throw new Error('not a WAVE file');
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  let format = 0;
  let numChannels = 0;
  let sampleRate = 0;
  let bits = 0;
  let blockAlign = 0;
  let samples: Uint8Array | null = null;
  let o = 12;
  while (o + 8 <= data.length) {
    const id = chunkId(data, o);
    const size = view.getUint32(o + 4, true);
    const body = o + 8;
    if (id === 'fmt ') {
      format = view.getUint16(body, true);
      numChannels = view.getUint16(body + 2, true);
      sampleRate = view.getUint32(body + 4, true);
      blockAlign = view.getUint16(body + 12, true);
      bits = view.getUint16(body + 14, true);
      // WAVE_FORMAT_EXTENSIBLE: the real format is the first two bytes of the sub format GUID
      if (format === FORMAT_EXTENSIBLE && size >= 26) format = view.getUint16(body + 24, true);
    } else if (id === 'data') {
      // some writers put a wrong size on the last chunk: clamp to the file
      samples = data.subarray(body, Math.min(data.length, body + size));
    }
    o = body + size + (size & 1);
  }
  if (!format || !samples) throw new Error('WAVE file without fmt or data');
  if (format !== FORMAT_PCM && format !== FORMAT_FLOAT) throw new Error(`unsupported WAVE format ${format}`);
  if (numChannels < 1 || !sampleRate) throw new Error('bad WAVE header');
  const bytes = bits >> 3;
  if (![1, 2, 3, 4].includes(bytes) || (format === FORMAT_FLOAT && bytes !== 4)) throw new Error(`unsupported WAVE sample size ${bits}`);
  const frame = blockAlign || bytes * numChannels;
  const frames = Math.floor(samples.length / frame);
  const sv = new DataView(samples.buffer, samples.byteOffset, samples.byteLength);
  const channels = Array.from({ length: numChannels }, () => new Float32Array(frames));
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < numChannels; c++) {
      const p = i * frame + c * bytes;
      let v: number;
      if (format === FORMAT_FLOAT) v = sv.getFloat32(p, true);
      else if (bytes === 1) v = (samples[p] - 128) / 128; // 8 bit is unsigned
      else if (bytes === 2) v = sv.getInt16(p, true) / 32768;
      else if (bytes === 3) v = ((samples[p] | (samples[p + 1] << 8) | (samples[p + 2] << 16)) << 8 >> 8) / 8388608;
      else v = sv.getInt32(p, true) / 2147483648;
      channels[c][i] = v;
    }
  }
  return { sampleRate, channels };
}
